import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Code2, Loader2, GitCommit } from 'lucide-react';
import { apiFetch } from '../../hooks/useFetch';
import Sparkline from './Sparkline';

/**
 * Compact "Deployed Xh ago · commit a4b3c2f" pill. Reads the latest
 * commit from /api/admin/codebase-stats (which shells out to
 * `git log -1`). Combined with the sparkline directly below it, an
 * admin can read "this deploy happened here → that's when the cold
 * start got slower" in a single glance.
 */
function LastDeployPill({ deploy }) {
  if (!deploy?.committed_at) return null;
  const when = new Date(deploy.committed_at);
  const diffMs = Date.now() - when.getTime();
  const mins = Math.max(0, Math.floor(diffMs / 60000));
  const ageLabel =
    mins < 1
      ? 'just now'
      : mins < 60
      ? `${mins}m ago`
      : mins < 60 * 24
      ? `${Math.floor(mins / 60)}h ago`
      : `${Math.floor(mins / (60 * 24))}d ago`;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"
      title={`${deploy.subject}\n${when.toLocaleString()}`}
      data-testid="refactor-win-last-deploy"
    >
      <GitCommit className="h-3 w-3" aria-hidden="true" />
      <span>Deployed {ageLabel}</span>
      <code className="font-mono opacity-70">{deploy.commit}</code>
    </span>
  );
}

/**
 * "Refactor Win" tile for the admin Overview tab. Calls
 * GET /api/admin/codebase-stats and renders a 4-section breakdown
 * (frontend pages, frontend components, backend routes, backend services)
 * plus a grand total. The number is *boring* to most users but
 * incredibly satisfying to track as we trim the codebase over time.
 *
 * Refreshes on mount; cheap enough that we don't bother caching.
 */
export default function RefactorWinTile() {
  const [stats, setStats] = useState(null);
  const [audit, setAudit] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [statsRes, auditRes, trendRes] = await Promise.all([
          apiFetch('/api/admin/codebase-stats'),
          apiFetch('/api/admin/startup-audit/latest'),
          apiFetch('/api/admin/startup-audit/trend?limit=30'),
        ]);
        if (cancelled) return;
        if (statsRes.error || !statsRes.data) {
          setError(statsRes.error || 'Could not load codebase stats');
        } else {
          setStats(statsRes.data);
        }
        if (!auditRes.error && auditRes.data) {
          setAudit(auditRes.data);
        }
        if (!trendRes.error && trendRes.data) {
          setTrend(trendRes.data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = stats
    ? [
        { label: 'Frontend pages', ...stats.frontend_pages },
        { label: 'Frontend components', ...stats.frontend_components },
        { label: 'Backend routes', ...stats.backend_routes },
        { label: 'Backend services', ...stats.backend_services },
      ]
    : [];

  return (
    <Card className="mb-6" data-testid="refactor-win-tile">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <Code2 className="h-5 w-5 text-[#00E5CC]" />
            Codebase Health
          </CardTitle>
          {stats?.last_deploy && (
            <LastDeployPill deploy={stats.last_deploy} />
          )}
        </div>
        <CardDescription className="text-xs">
          Total lines of code across pages, components, and backend.
          Lower is better.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Counting lines…
          </div>
        )}
        {error && (
          <p className="text-sm text-destructive" data-testid="refactor-win-error">
            {error}
          </p>
        )}
        {stats && !error && (
          <div className="space-y-2">
            <div className="rounded-lg bg-gradient-to-r from-[#00E5CC]/10 to-purple-500/10 p-4 text-center">
              <p className="text-3xl font-bold tabular-nums" data-testid="refactor-win-grand-total">
                {stats.grand_total_lines.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                lines across {stats.grand_total_files.toLocaleString()} files
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2 text-sm">
              {rows.map((row) => (
                <div
                  key={row.label}
                  className="rounded border p-2"
                  data-testid={`refactor-win-row-${row.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <p className="text-xs text-muted-foreground">{row.label}</p>
                  <p className="font-semibold tabular-nums">
                    {row.lines.toLocaleString()}{' '}
                    <span className="text-xs text-muted-foreground">
                      ({row.files} files)
                    </span>
                  </p>
                </div>
              ))}
            </div>

            {/* Latest cold-start timings — early-warning for deploy drift */}
            {audit && audit.task_count > 0 && (
              <div className="pt-3 mt-3 border-t" data-testid="refactor-win-audit">
                <div className="flex items-center justify-between mb-2 gap-3">
                  <p className="text-xs font-medium text-muted-foreground">
                    Last cold start
                  </p>
                  <div className="flex items-center gap-2 min-w-0">
                    {trend && trend.count >= 2 && (
                      <Sparkline
                        values={trend.points.map((p) => p.total_ms)}
                        width={96}
                        height={20}
                        className="shrink-0"
                        testid="refactor-win-trend-sparkline"
                      />
                    )}
                    <p
                      className="text-xs tabular-nums text-muted-foreground whitespace-nowrap"
                      data-testid="refactor-win-audit-total"
                    >
                      {(audit.total_ms / 1000).toFixed(1)}s ·{' '}
                      {audit.task_count} task{audit.task_count === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  {audit.tasks.map((t) => {
                    const ms = t.duration_ms || 0;
                    const pct = audit.total_ms > 0
                      ? Math.min(100, (ms / audit.total_ms) * 100)
                      : 0;
                    return (
                      <div
                        key={t.task}
                        className="text-xs"
                        data-testid={`refactor-win-task-${t.task}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate">{t.task}</span>
                          <span
                            className={`tabular-nums ml-2 ${
                              t.error ? 'text-destructive' : 'text-muted-foreground'
                            }`}
                          >
                            {ms.toLocaleString()} ms{t.error ? ' · failed' : ''}
                          </span>
                        </div>
                        <div className="h-1 bg-muted rounded mt-0.5 overflow-hidden">
                          <div
                            className={`h-full ${
                              t.error
                                ? 'bg-destructive'
                                : 'bg-gradient-to-r from-[#00E5CC] to-purple-500'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
